import { Injectable } from '@angular/core';
import * as d3 from 'd3';

@Injectable()
export class VizD3Service {
  getStackedBarColors(vizStackedBarKeys) {
    const light = d3
      .scaleOrdinal()
      .domain(vizStackedBarKeys)
      .range(['#e19fa6', '#ffe280', '#8ca3be', '#80d8e5', '#e6e6e6']); // light

    const regular = d3
      .scaleOrdinal()
      .domain(vizStackedBarKeys)
      .range(['#d26e79', '#ffd64c', '#52759e', '#40c4d8', '#d9d9d9']); // base

    const bright = d3
      .scaleOrdinal()
      .domain(vizStackedBarKeys)
      .range(['#c23e4d', '#ffc400', '#19477d', '#00b0ca', '#cccccc']); // full

    return { light, regular, bright };
  }

  buildMouseTarget(
    mouseTarget,
    width: number,
    marginLeft: number,
    height: number,
  ): d3.Selection<SVGGElement, any, any, any> {
    return mouseTarget
      .append('rect')
      .attr('class', 'mouseTarget')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width - marginLeft)
      .attr('height', height)
      .style('opacity', 0);
  }

  createXScale(data, margin, width) {
    return d3
      .scaleBand()
      .domain(data.map((d: any) => d.group))
      .range([margin.left, width - margin.right])
      .paddingInner(0.3)
      .paddingOuter(0.7);
  }

  createYScale(yAxisDomainMax, margin, height) {
    return d3
      .scaleLinear()
      .domain([0, yAxisDomainMax])
      .nice()
      .range([height - margin.bottom, margin.top]);
  }

  createYScaleNoData(margin, height) {
    return d3
      .scaleLinear()
      .domain([0, 5])
      .nice()
      .range([height - margin.bottom, margin.top]);
  }

  createXAxis(height, margin, x, g) {
    return g
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .attr('class', 'x-axis')
      .call(d3.axisBottom(x).tickSizeOuter(0))
      .call(g => g.select('.domain').remove());
  }

  createYAxis(width, margin, y, g) {
    return g
      .attr('transform', `translate(${margin.left},0)`)
      .attr('class', 'y-axis')
      .call(
        d3
          .axisLeft(y)
          .ticks(5)
          .tickSize(-width + margin.left + margin.right),
      )
      .call(g => g.select('.domain').remove());
  }

  createYAxisNoData(width, margin, yNoData, g) {
    return g
      .attr('transform', `translate(${margin.left},0)`)
      .attr('class', 'y-axis')
      .call(
        d3
          .axisLeft(yNoData)
          .ticks(5)
          .tickFormat(d => {
            return '';
          })
          .tickSize(-width + margin.left + margin.right),
      )
      .call(g => g.select('.domain').remove());
  }

  createVizTitle(vizTitle, addTransition, title) {
    vizTitle
      .selectAll('.viz-title')
      .remove()
      .exit();
    vizTitle
      .append('text')
      .attr('class', 'viz-title')
      .attr('x', 20)
      .attr('y', 15)
      .text(title)
      .style('opacity', 0)
      .transition()
      .duration(addTransition ? 1000 : 0)
      .style('opacity', 0.6);
  }

  getRectanglesPreTransition(x, y, d): string {
    const rx = d.value > 0 ? 4 : 0;
    const ry = d.value > 0 ? 4 : 0;
    return `
      M${x(d.group)},${y(0)}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${0}
      h${-x.bandwidth()}
      Z
    `;
  }

  getRectanglesPostTransition(x, y, d, height, margin): string {
    const rx = d.value > 0 ? 4 : 0;
    const ry = d.value > 0 ? 4 : 0;
    return `
      M${x(d.group)},${y(d.value) + ry}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${height - margin.bottom - y(d.value) - ry}
      h${-x.bandwidth()}
      Z
    `;
  }

  getStackedBarsPreTransition(x, y, d): string {
    const rx = 4;
    const ry = 4;
    return `
      M${x(d.data.group)},${y(0)}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${0}
      h${-x.bandwidth()}
      Z
    `;
  }

  getStackedBarsPostTransition(x, y, d, height, margin, rx, ry): string {
    return `
      M${x(d.data.group)},${y(d[1]) + ry}
      a${rx},${ry} 0 0 1 ${rx},${-ry}
      h${x.bandwidth() - 2 * rx}
      a${rx},${ry} 0 0 1 ${rx},${ry}
      v${height - margin.bottom - y(d[1] - d[0]) - ry}
      h${-x.bandwidth()}
      Z
    `;
  }
}
